/** Wildly Important Goals: the board, the conversion wizard, and the weekly cycle. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface WigPerson {
  userId: string
  displayName: string
  email: string
}

export interface TrendPoint {
  weekStart: string
  value: number | null
}

export interface LagMeasure {
  id: string
  name: string
  unit: string
  source: 'manual' | 'tracker' | 'milestones'
  trackerItemId: string | null
  trackerStale: boolean
  direction: 'up' | 'down'
  baseline: number | null
  target: number | null
  /** The target is the tracker's or milestones' own total, and moves with it. */
  targetFollowsSource: boolean
  current: number | null
  /** Where the pace line says it should be today. */
  expected: number | null
  /** Fraction of the goal behind pace; negative is ahead. */
  behind: number | null
  status: 'green' | 'amber' | 'red' | null
  trend: TrendPoint[]
}

export interface LeadMeasure {
  id: string
  name: string
  unit: string
  weeklyTarget: number | null
  thisWeek: number
  lastWeek: number
  missedTwoWeeks: boolean
  trend: TrendPoint[]
}

export interface Commitment {
  id: string
  person: WigPerson
  text: string
  weekStart: string
  status: 'open' | 'kept' | 'missed'
  mine: boolean
  rowVersion: number
}

export type HealthStatus = 'green' | 'amber' | 'red'

export interface WigLane {
  id: string
  projectId: string
  projectName: string
  team: string
  teamShortLabel: string
  projectRag: string
  status: 'active' | 'retired'
  fromText: string
  toText: string
  targetDate: string | null
  startDate: string
  sessionDay: number
  sessionDayLabel: string
  weeksLeft: number | null
  fridaysLeft: number | null
  owner: WigPerson
  supporters: WigPerson[]
  /** True once there is a lag and a lead measure — the WIG then sets the project's health. */
  governsHealth: boolean
  health: { status: HealthStatus; reasons: string[] } | null
  lag: LagMeasure[]
  lead: LeadMeasure[]
  weekStart: string
  commitments: {
    thisWeek: Commitment[]
    lastWeek: Commitment[]
    lastWeekKept: number
    lastWeekTotal: number
  }
  retiredReason: string
  canEdit: boolean
  canRecord: boolean
  canCommit: boolean
  rowVersion: number
}

export interface WigDraft {
  project: {
    id: string
    name: string
    team: string
    teamShortLabel: string
    status: string
    targetDate: string | null
    ownerUserId: string | null
    ownerName: string
  }
  existingWigId: string | null
  checks: { ok: boolean; text: string; blocking: boolean }[]
  people: { userId: string; displayName: string; email: string; ownsWig: string | null }[]
  suggestedOwnerId: string | null
  suggestedSupporterIds: string[]
  lagOptions: {
    source: 'tracker' | 'milestones'
    trackerItemId: string | null
    name: string
    unit: string
    current: number | null
    target: number | null
    detail: string
  }[]
  today: string
  weeksLeft: number | null
}

export interface MeasureInput {
  id?: string
  kind: 'lag' | 'lead'
  name: string
  unit: string
  source: 'manual' | 'tracker' | 'milestones'
  tracker_item_id: string | null
  baseline: number | null
  target: number | null
  weekly_target: number | null
}

const wigKey = ['wigs'] as const

function useInvalidate() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: wigKey })
    // Health, the WIG badge and the drawer card all come from the project too.
    void client.invalidateQueries({ queryKey: ['projects'] })
  }
}

export function useWigs(team?: string) {
  return useQuery({
    queryKey: [...wigKey, 'board', team ?? 'all'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/wigs', {
        params: { query: team ? { team: team as never } : {} },
      })
      return data as unknown as { lanes: WigLane[]; canDefine: boolean }
    },
  })
}

export function useProjectWig(projectId: string, enabled = true) {
  return useQuery({
    queryKey: [...wigKey, 'project', projectId],
    enabled: enabled && Boolean(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/wig', {
        params: { path: { project_id: projectId } },
      })
      return (data as unknown as { wig: WigLane | null }).wig
    },
  })
}

export function useWigDraft(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...wigKey, 'draft', projectId],
    enabled,
    // The draft reads the project as it stands; always fresh when the wizard opens.
    staleTime: 0,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/wig-draft', {
        params: { path: { project_id: projectId } },
      })
      return data as unknown as WigDraft
    },
  })
}

export function useCreateWig() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.POST('/api/v1/wigs', { body: body as never })
      return data as unknown as WigLane
    },
    onSuccess: invalidate,
  })
}

export function useUpdateWig() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const { data } = await api.PATCH('/api/v1/wigs/{wig_id}', {
        params: { path: { wig_id: id } },
        body: body as never,
      })
      return data as unknown as WigLane
    },
    onSuccess: invalidate,
  })
}

export function useRetireWig() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.POST('/api/v1/wigs/{wig_id}/retire', {
        params: { path: { wig_id: id } },
        body: { reason } as never,
      })
    },
    onSuccess: invalidate,
  })
}

/** A lead measure's count for a week, or a typed lag measure's current number. */
export function useRecordEntry() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({
      measureId,
      value,
      week,
    }: {
      measureId: string
      value: number
      week?: string
    }) => {
      await api.PUT('/api/v1/wig-measures/{measure_id}/entry', {
        params: { path: { measure_id: measureId } },
        body: { value, ...(week ? { week } : {}) } as never,
      })
    },
    onSuccess: invalidate,
  })
}

export function useAddCommitment() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ wigId, text }: { wigId: string; text: string }) => {
      await api.POST('/api/v1/wigs/{wig_id}/commitments', {
        params: { path: { wig_id: wigId } },
        body: { text } as never,
      })
    },
    onSuccess: invalidate,
  })
}

export function useUpdateCommitment() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      text?: string
      status?: Commitment['status']
      row_version?: number
    }) => {
      await api.PATCH('/api/v1/wig-commitments/{commitment_id}', {
        params: { path: { commitment_id: id } },
        body: body as never,
      })
    },
    onSuccess: invalidate,
  })
}

export function useDeleteCommitment() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.DELETE('/api/v1/wig-commitments/{commitment_id}', {
        params: { path: { commitment_id: id } },
      })
    },
    onSuccess: invalidate,
  })
}

/** Counts read as counts: 1,472 rather than 1472.0. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return Math.abs(value - Math.round(value)) < 0.05
    ? Math.round(value).toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

/** Progress along the journey from baseline to target, 0 to 1, in either direction. */
export function progressFraction(
  current: number | null,
  baseline: number | null,
  target: number | null,
): number {
  if (current === null || baseline === null || target === null || target === baseline) return 0
  return Math.max(0, Math.min(1, (current - baseline) / (target - baseline)))
}

/** The Monday of the week before `weekStart` (an ISO date), as an ISO date. */
export function previousWeek(weekStart: string): string {
  const day = new Date(`${weekStart}T00:00:00`)
  day.setDate(day.getDate() - 7)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

/** "/ 110" for a measure that climbs to its target, "→ 0" for one that falls to it —
 * "132 / 0" would read as 132 out of nothing. */
export function towards(lag: { direction: 'up' | 'down'; target: number | null }): string {
  return `${lag.direction === 'down' ? '→' : '/'} ${formatNumber(lag.target)}`
}

/** The WIG scoreboard section of Monday's digest, as it would be sent now. */
export function useWigDigestPreview(enabled: boolean) {
  return useQuery({
    queryKey: [...wigKey, 'digest-preview'],
    enabled,
    staleTime: 0,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/wigs/digest-preview')
      return data as unknown as { html: string; recipientCount: number }
    },
  })
}
